import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaUserShield,
  FaShieldAlt,
  FaSyncAlt,
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaTimesCircle,
  FaCheckDouble,
  FaBan,
  FaCopy,
  FaUserTie,
  FaGraduationCap,
  FaMoneyBillWave,
  FaBus,
  FaCalendarCheck,
  FaClipboardList,
  FaComments,
  FaFileInvoiceDollar,
  FaCog,
  FaBook,
  FaLayerGroup,
  FaIdBadge,
  FaPhoneAlt,
  FaBuilding,
  FaAward,
  FaSlidersH,
  FaSpinner,
  FaInfoCircle,
  FaHome,
} from "react-icons/fa";

import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";

import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/staff-privilege.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

/**
 * Get contextual React Icon for a module
 */
function getModuleIcon(module) {
  const text = [
    module?.module_name,
    module?.module_activity,
    module?.component_name,
    module?.package_name,
    module?.module_type,
    module?.software_section,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    text.includes("fee") ||
    text.includes("due") ||
    text.includes("payment") ||
    text.includes("receipt") ||
    text.includes("revenue") ||
    text.includes("payroll") ||
    text.includes("salary") ||
    text.includes("expense") ||
    text.includes("settlement") ||
    text.includes("fine") ||
    text.includes("advance")
  ) {
    return <FaMoneyBillWave />;
  }
  if (
    text.includes("attend") ||
    text.includes("biometric") ||
    text.includes("leave") ||
    text.includes("holiday")
  ) {
    return <FaCalendarCheck />;
  }
  if (
    text.includes("transport") ||
    text.includes("vehicle") ||
    text.includes("route") ||
    text.includes("driver")
  ) {
    return <FaBus />;
  }
  if (
    text.includes("student") ||
    text.includes("admission") ||
    text.includes("sibling") ||
    text.includes("rte") ||
    text.includes("promote") ||
    text.includes("certificate") ||
    text.includes("tc") ||
    text.includes("apaar") ||
    text.includes("aadhaar")
  ) {
    return <FaGraduationCap />;
  }
  if (
    text.includes("notice") ||
    text.includes("notification") ||
    text.includes("message") ||
    text.includes("whatsapp") ||
    text.includes("sms") ||
    text.includes("comment") ||
    text.includes("helpdesk")
  ) {
    return <FaComments />;
  }
  if (
    text.includes("exam") ||
    text.includes("mark") ||
    text.includes("result") ||
    text.includes("paper") ||
    text.includes("datesheet") ||
    text.includes("report card")
  ) {
    return <FaClipboardList />;
  }
  return <FaBook />;
}

/**
 * Get staff initials from name
 */
function getInitials(name) {
  if (!name) return "ST";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

const StaffPrivilege = () => {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  // Master Data State
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [privilegeMap, setPrivilegeMap] = useState({}); // { [staffId]: [modules...] }

  // UI & Search Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [deptFilter, setDeptFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, HAS_GRANTED, NO_GRANTED

  // Expanded staff set
  const [expandedStaffIds, setExpandedStaffIds] = useState(new Set());
  const [privilegeLoadingId, setPrivilegeLoadingId] = useState("");
  const [updatingKey, setUpdatingKey] = useState(""); // `${staffId}|${moduleId}` or `${staffId}|batch`

  // Per-staff module search filter
  const [moduleSearchMap, setModuleSearchMap] = useState({}); // { [staffId]: string }

  // Copy Privileges Modal State
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [targetStaff, setTargetStaff] = useState(null);
  const [selectedSourceStaffId, setSelectedSourceStaffId] = useState("");
  const [copyingPrivileges, setCopyingPrivileges] = useState(false);

  /**
   * Fetch All Staff
   */
  const loadStaff = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing. Please re-login.");
      setStaffList([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaff.php`, {
        params: { school_code: schoolCode },
      });

      if (res.data?.api_status === "OK" && Array.isArray(res.data?.staff)) {
        setStaffList(res.data.staff);
      } else {
        setStaffList([]);
        myToast.notify_warning(res.data?.api_remark || "No staff found.");
      }
    } catch (error) {
      console.error("[StaffPrivilege] loadStaff error:", error);
      setStaffList([]);
      myToast.notify_error("Failed to load staff list.");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch privileges for a specific staff member
   */
  const loadStaffPrivileges = useCallback(async (staffId, forceReload = false) => {
    const schoolCode = SessionManager.getSchoolCode();
    const id = String(staffId);
    if (!schoolCode || !id) return null;

    if (!forceReload && privilegeMap[id]) {
      return privilegeMap[id];
    }

    setPrivilegeLoadingId(id);
    try {
      const res = await axios.get(`${API_BASE()}getStaffPrivilege.php`, {
        params: {
          school_code: schoolCode,
          staff_id: id,
        },
      });

      if (res.data?.api_status === "OK" && Array.isArray(res.data?.data)) {
        const modules = res.data.data;
        setPrivilegeMap((prev) => ({
          ...prev,
          [id]: modules,
        }));
        return modules;
      } else {
        setPrivilegeMap((prev) => ({
          ...prev,
          [id]: [],
        }));
        myToast.notify_warning(
          res.data?.api_remark || "Could not load privileges for staff."
        );
        return [];
      }
    } catch (error) {
      console.error(`[StaffPrivilege] loadStaffPrivileges error for staff ${id}:`, error);
      myToast.notify_error("Network error while fetching privileges.");
      return [];
    } finally {
      setPrivilegeLoadingId("");
    }
  }, [privilegeMap]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  /**
   * Distinct Roles and Departments for Filters
   */
  const availableRoles = useMemo(() => {
    const roles = new Set();
    staffList.forEach((s) => {
      if (s.staff_role && s.staff_role.trim()) {
        roles.add(s.staff_role.trim());
      }
    });
    return Array.from(roles).sort();
  }, [staffList]);

  const availableDepts = useMemo(() => {
    const depts = new Set();
    staffList.forEach((s) => {
      if (s.department_name && s.department_name.trim()) {
        depts.add(s.department_name.trim());
      }
    });
    return Array.from(depts).sort();
  }, [staffList]);

  /**
   * Filtered Staff List
   */
  const filteredStaffList = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return staffList.filter((staff) => {
      // Role filter
      if (roleFilter !== "ALL" && staff.staff_role?.trim() !== roleFilter) {
        return false;
      }

      // Department filter
      if (deptFilter !== "ALL" && staff.department_name?.trim() !== deptFilter) {
        return false;
      }

      // Privilege Status filter (if privileges are loaded in map)
      const staffId = String(staff.staff_id);
      const modules = privilegeMap[staffId];
      if (modules && statusFilter !== "ALL") {
        const grantedCount = modules.filter((m) => m.status === "Granted").length;
        if (statusFilter === "HAS_GRANTED" && grantedCount === 0) return false;
        if (statusFilter === "NO_GRANTED" && grantedCount > 0) return false;
      }

      // Search Query
      if (query) {
        const searchBlob = [
          staff.staff_name,
          staff.staff_role,
          staff.staff_id,
          staff.department_name,
          staff.mobile_number,
          staff.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchBlob.includes(query)) return false;
      }

      return true;
    });
  }, [staffList, searchQuery, roleFilter, deptFilter, statusFilter, privilegeMap]);

  /**
   * Global Granted Count Stats
   */
  const totalGrantedStats = useMemo(() => {
    let totalGranted = 0;
    Object.values(privilegeMap).forEach((modules) => {
      if (Array.isArray(modules)) {
        totalGranted += modules.filter((m) => m.status === "Granted").length;
      }
    });
    return totalGranted;
  }, [privilegeMap]);

  /**
   * Toggle Staff Accordion Expand/Collapse
   */
  const toggleStaffExpand = async (staffId) => {
    const id = String(staffId);
    setExpandedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    if (!privilegeMap[id]) {
      await loadStaffPrivileges(id);
    }
  };

  /**
   * Expand All / Collapse All
   */
  const handleExpandAll = async () => {
    const allIds = filteredStaffList.map((s) => String(s.staff_id));
    setExpandedStaffIds(new Set(allIds));

    // Pre-fetch privileges for all currently visible staff
    for (const staff of filteredStaffList) {
      const id = String(staff.staff_id);
      if (!privilegeMap[id]) {
        await loadStaffPrivileges(id);
      }
    }
  };

  const handleCollapseAll = () => {
    setExpandedStaffIds(new Set());
  };

  /**
   * Toggle Individual Privilege Module
   */
  const handleTogglePrivilege = async (staffId, moduleId, currentStatus) => {
    const schoolCode = SessionManager.getSchoolCode();
    const id = String(staffId);
    const mId = String(moduleId);
    if (!schoolCode || !id || !mId) return;

    const nextStatus = currentStatus === "Granted" ? "InActive" : "Active";
    const nextLabel = nextStatus === "Active" ? "Granted" : "Denied";
    const updateKey = `${id}|${mId}`;

    // Optimistic Update
    setPrivilegeMap((prev) => {
      const currentModules = prev[id] || [];
      return {
        ...prev,
        [id]: currentModules.map((m) =>
          String(m.module_id) === mId ? { ...m, status: nextLabel } : m
        ),
      };
    });

    setUpdatingKey(updateKey);
    try {
      const res = await axios.get(`${API_BASE()}updateStaffPrivilege.php`, {
        params: {
          school_code: schoolCode,
          staff_id: id,
          module_id: mId,
          status: nextStatus,
        },
      });

      if (res.data?.api_status === "OK") {
        myToast.notify_success(`Privilege updated: ${nextLabel}`);
      } else {
        // Rollback on error
        setPrivilegeMap((prev) => {
          const currentModules = prev[id] || [];
          return {
            ...prev,
            [id]: currentModules.map((m) =>
              String(m.module_id) === mId ? { ...m, status: currentStatus } : m
            ),
          };
        });
        myToast.notify_warning(res.data?.api_remark || "Failed to update privilege.");
      }
    } catch (error) {
      console.error("[StaffPrivilege] update error:", error);
      // Rollback on network error
      setPrivilegeMap((prev) => {
        const currentModules = prev[id] || [];
        return {
          ...prev,
          [id]: currentModules.map((m) =>
            String(m.module_id) === mId ? { ...m, status: currentStatus } : m
          ),
        };
      });
      myToast.notify_error("Network error updating privilege.");
    } finally {
      setUpdatingKey("");
    }
  };

  /**
   * Batch Grant All Modules for a Staff Member
   */
  const handleBatchGrantAll = async (staffId, staffName) => {
    const schoolCode = SessionManager.getSchoolCode();
    const id = String(staffId);
    const modules = privilegeMap[id] || [];
    const deniedModules = modules.filter((m) => m.status !== "Granted");

    if (deniedModules.length === 0) {
      myToast.notify_info("All privileges are already granted for this staff member.");
      return;
    }

    const confirmMsg = `Grant ALL ${deniedModules.length} remaining modules to ${staffName || "this staff member"}?`;
    if (!window.confirm(confirmMsg)) return;

    setUpdatingKey(`${id}|batch`);

    // Optimistically grant all
    setPrivilegeMap((prev) => ({
      ...prev,
      [id]: (prev[id] || []).map((m) => ({ ...m, status: "Granted" })),
    }));

    try {
      const promises = deniedModules.map((m) =>
        axios.get(`${API_BASE()}updateStaffPrivilege.php`, {
          params: {
            school_code: schoolCode,
            staff_id: id,
            module_id: m.module_id,
            status: "Active",
          },
        })
      );

      await Promise.all(promises);
      myToast.notify_success(`All privileges successfully granted to ${staffName}!`);
    } catch (error) {
      console.error("[StaffPrivilege] batch grant error:", error);
      myToast.notify_error("Some privileges could not be updated. Reloading...");
      await loadStaffPrivileges(id, true);
    } finally {
      setUpdatingKey("");
    }
  };

  /**
   * Batch Revoke All Modules for a Staff Member
   */
  const handleBatchRevokeAll = async (staffId, staffName) => {
    const schoolCode = SessionManager.getSchoolCode();
    const id = String(staffId);
    const modules = privilegeMap[id] || [];
    const grantedModules = modules.filter((m) => m.status === "Granted");

    if (grantedModules.length === 0) {
      myToast.notify_info("No granted privileges to revoke for this staff member.");
      return;
    }

    const confirmMsg = `Revoke ALL ${grantedModules.length} privileges from ${staffName || "this staff member"}?`;
    if (!window.confirm(confirmMsg)) return;

    setUpdatingKey(`${id}|batch`);

    // Optimistically revoke all
    setPrivilegeMap((prev) => ({
      ...prev,
      [id]: (prev[id] || []).map((m) => ({ ...m, status: "Denied" })),
    }));

    try {
      const promises = grantedModules.map((m) =>
        axios.get(`${API_BASE()}updateStaffPrivilege.php`, {
          params: {
            school_code: schoolCode,
            staff_id: id,
            module_id: m.module_id,
            status: "InActive",
          },
        })
      );

      await Promise.all(promises);
      myToast.notify_success(`All privileges revoked for ${staffName}.`);
    } catch (error) {
      console.error("[StaffPrivilege] batch revoke error:", error);
      myToast.notify_error("Some privileges could not be updated. Reloading...");
      await loadStaffPrivileges(id, true);
    } finally {
      setUpdatingKey("");
    }
  };

  /**
   * Open Copy Privileges Modal
   */
  const openCopyModal = async (staff) => {
    setTargetStaff(staff);
    setSelectedSourceStaffId("");
    setCopyModalOpen(true);

    const targetId = String(staff.staff_id);
    if (!privilegeMap[targetId]) {
      await loadStaffPrivileges(targetId);
    }
  };

  /**
   * Execute Copy Privileges from Source Staff to Target Staff
   */
  const executeCopyPrivileges = async () => {
    if (!targetStaff || !selectedSourceStaffId) {
      myToast.notify_warning("Please select a staff member to copy privileges from.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    const targetId = String(targetStaff.staff_id);
    const sourceId = String(selectedSourceStaffId);

    setCopyingPrivileges(true);
    try {
      // 1. Ensure source staff privileges are loaded
      let sourceModules = privilegeMap[sourceId];
      if (!sourceModules) {
        sourceModules = await loadStaffPrivileges(sourceId, true);
      }

      // 2. Ensure target staff privileges are loaded
      let targetModules = privilegeMap[targetId];
      if (!targetModules) {
        targetModules = await loadStaffPrivileges(targetId, true);
      }

      if (!sourceModules || !targetModules) {
        myToast.notify_error("Failed to load privilege profiles for comparison.");
        return;
      }

      // 3. Map of desired status from source
      const sourceStatusMap = {};
      sourceModules.forEach((m) => {
        sourceStatusMap[String(m.module_id)] = m.status === "Granted" ? "Active" : "InActive";
      });

      // 4. Calculate diffs to update
      const updates = [];
      targetModules.forEach((m) => {
        const mId = String(m.module_id);
        const currentTargetStatus = m.status === "Granted" ? "Active" : "InActive";
        const desiredStatus = sourceStatusMap[mId] || "InActive";

        if (currentTargetStatus !== desiredStatus) {
          updates.push({
            module_id: mId,
            status: desiredStatus,
          });
        }
      });

      if (updates.length === 0) {
        myToast.notify_info("Privileges are already identical between both staff members.");
        setCopyModalOpen(false);
        return;
      }

      // 5. Apply all updates
      const promises = updates.map((u) =>
        axios.get(`${API_BASE()}updateStaffPrivilege.php`, {
          params: {
            school_code: schoolCode,
            staff_id: targetId,
            module_id: u.module_id,
            status: u.status,
          },
        })
      );

      await Promise.all(promises);
      myToast.notify_success(`Successfully synced ${updates.length} privileges to ${targetStaff.staff_name}!`);

      // Reload target staff privileges
      await loadStaffPrivileges(targetId, true);
      setCopyModalOpen(false);
    } catch (error) {
      console.error("[StaffPrivilege] copy privileges error:", error);
      myToast.notify_error("Failed to copy privileges completely.");
    } finally {
      setCopyingPrivileges(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page ad-page sp-page-container"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Content Header & Breadcrumbs */}
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
            title="Go to Admin Dashboard"
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Staff Privileges</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sp-shell">
        {/* Hero Header with 14px rounded corners matching AdmissionDashboard */}
        <header className="sp-hero" style={{ "--sp-hero-bg": HERO_GRADIENT }}>
          <div className="sp-hero__brand">
            <div className="sp-hero__icon-badge" aria-hidden="true">
              <FaUserShield />
            </div>
            <div>
              <h1 className="sp-hero__title">Staff Privilege Management</h1>
              <div className="sp-hero__subtitle">
                <span className="sp-hero__stat-pill">
                  <FaUserTie aria-hidden="true" />
                  {staffList.length} Total Staff
                </span>
                <span className="sp-hero__stat-pill">
                  <FaShieldAlt aria-hidden="true" />
                  {totalGrantedStats} Module Permissions Active
                </span>
                {filteredStaffList.length !== staffList.length && (
                  <span className="sp-hero__stat-pill" style={{ background: "rgba(255, 255, 255, 0.35)" }}>
                    {filteredStaffList.length} Filtered
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="sp-hero__actions">
            <button
              type="button"
              className="sp-btn sp-btn--outline"
              onClick={handleExpandAll}
              title="Expand all staff cards"
            >
              <FaChevronDown />
              Expand All
            </button>
            <button
              type="button"
              className="sp-btn sp-btn--outline"
              onClick={handleCollapseAll}
              title="Collapse all staff cards"
            >
              <FaChevronUp />
              Collapse All
            </button>
            <button
              type="button"
              className={`sp-btn sp-btn--outline sp-btn--icon-only ${loading ? "spinning" : ""}`}
              onClick={loadStaff}
              disabled={loading}
              title="Refresh Staff List"
              aria-label="Refresh Staff List"
            >
              <FaSyncAlt />
            </button>
          </div>
        </header>

        {/* Toolbar & Filters */}
        <section className="sp-toolbar" aria-label="Staff Filters">
          <div className="sp-toolbar__left">
            {/* Search Input */}
            <div className="sp-search-wrapper">
              <FaSearch className="sp-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="sp-search-input"
                placeholder="Search staff name, role, ID, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search staff members"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="sp-clear-search"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Role Filter Dropdown */}
            <select
              className="sp-filter-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Filter by staff role"
            >
              <option value="ALL">All Roles ({staffList.length})</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>

            {/* Department Filter Dropdown */}
            {availableDepts.length > 0 && (
              <select
                className="sp-filter-select"
                value={deptFilter}
                onChange={(e) => setDeptFilter(e.target.value)}
                aria-label="Filter by department"
              >
                <option value="ALL">All Departments</option>
                {availableDepts.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            )}

            {/* Privilege Status Filter */}
            <select
              className="sp-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by privilege status"
            >
              <option value="ALL">All Privilege States</option>
              <option value="HAS_GRANTED">Has Active Privileges</option>
              <option value="NO_GRANTED">No Active Privileges</option>
            </select>
          </div>

          <div className="sp-toolbar__right">
            <span style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
              Showing {filteredStaffList.length} of {staffList.length} staff
            </span>
          </div>
        </section>

        {/* Staff List Body */}
        <main className="sp-body">
          {filteredStaffList.length === 0 ? (
            <div className="sp-empty-state">
              <div className="sp-empty-state__icon">
                <FaUserTie />
              </div>
              <h3 className="sp-empty-state__title">No Staff Found</h3>
              <p className="sp-empty-state__text">
                {searchQuery || roleFilter !== "ALL" || deptFilter !== "ALL"
                  ? "No staff members match the selected search criteria or filters."
                  : "No staff records are currently available in the database."}
              </p>
              {(searchQuery || roleFilter !== "ALL" || deptFilter !== "ALL") && (
                <button
                  type="button"
                  className="sp-btn sp-btn--outline sp-btn--sm"
                  style={{ marginTop: 14 }}
                  onClick={() => {
                    setSearchQuery("");
                    setRoleFilter("ALL");
                    setDeptFilter("ALL");
                    setStatusFilter("ALL");
                  }}
                >
                  Reset All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="sp-list">
              {filteredStaffList.map((staff) => {
                const staffId = String(staff.staff_id);
                const isExpanded = expandedStaffIds.has(staffId);
                const modules = privilegeMap[staffId] || [];
                const isLoadingPrivileges = privilegeLoadingId === staffId;
                const isUpdatingBatch = updatingKey === `${staffId}|batch`;

                const grantedModules = modules.filter((m) => m.status === "Granted");
                const grantedCount = grantedModules.length;
                const totalModulesCount = modules.length;
                const grantedPercent =
                  totalModulesCount > 0
                    ? Math.round((grantedCount / totalModulesCount) * 100)
                    : 0;

                // Per-staff module search filter
                const moduleSearch = (moduleSearchMap[staffId] || "").toLowerCase();

                // Filtered modules inside this staff card
                const filteredModules = modules.filter((mod) => {
                  if (moduleSearch) {
                    const modBlob = [mod.module_name, mod.module_type, mod.module_activity]
                      .filter(Boolean)
                      .join(" ")
                      .toLowerCase();
                    if (!modBlob.includes(moduleSearch)) return false;
                  }
                  return true;
                });

                return (
                  <article
                    key={staffId}
                    className={`sp-card ${isExpanded ? "sp-card--expanded" : ""}`}
                  >
                    {/* Staff Card Header */}
                    <div
                      className="sp-card__header"
                      onClick={() => toggleStaffExpand(staffId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleStaffExpand(staffId);
                        }
                      }}
                      aria-expanded={isExpanded}
                    >
                      <div className="sp-card__profile">
                        {staff.staff_image_path &&
                          staff.staff_image_path !== "Staff Images Path" &&
                          !staff.staff_image_path.includes(" ") ? (
                          <div className="sp-avatar">
                            <img
                              src={staff.staff_image_path}
                              alt={staff.staff_name || "Staff"}
                              className="sp-avatar__img"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        ) : (
                          <div className="sp-avatar" aria-hidden="true">
                            {getInitials(staff.staff_name)}
                          </div>
                        )}

                        <div className="sp-card__info">
                          <div className="sp-card__name-row">
                            <h2 className="sp-card__name">
                              {staff.staff_name || "Unnamed Staff"}
                            </h2>
                            {staff.staff_role && (
                              <span className="sp-badge sp-badge--role">
                                {staff.staff_role}
                              </span>
                            )}
                            {staff.department_name && (
                              <span className="sp-badge sp-badge--dept">
                                <FaBuilding style={{ marginRight: 3 }} />
                                {staff.department_name}
                              </span>
                            )}
                            <span className="sp-badge sp-badge--id">
                              ID: {staff.staff_id}
                            </span>
                          </div>

                          <div className="sp-card__sub-details">
                            {staff.mobile_number && (
                              <span>
                                <FaPhoneAlt style={{ marginRight: 4 }} />
                                {staff.mobile_number}
                              </span>
                            )}
                            {staff.email && <span>{staff.email}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Header Actions & Privilege Pill */}
                      <div
                        className="sp-card__meta"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {modules.length > 0 ? (
                          <div
                            className="sp-progress-pill"
                            title={`${grantedCount} of ${totalModulesCount} modules granted (${grantedPercent}%)`}
                          >
                            <div className="sp-progress-pill__bar">
                              <div
                                className="sp-progress-pill__fill"
                                style={{ width: `${grantedPercent}%` }}
                              />
                            </div>
                            <span>
                              {grantedCount}/{totalModulesCount} Granted
                            </span>
                          </div>
                        ) : (
                          <span
                            className="sp-progress-pill"
                            style={{ opacity: 0.8 }}
                          >
                            Privileges
                          </span>
                        )}

                        <button
                          type="button"
                          className="sp-card__toggle-btn"
                          onClick={() => toggleStaffExpand(staffId)}
                          aria-label={isExpanded ? "Hide Privileges" : "Manage Privileges"}
                        >
                          {isExpanded ? (
                            <>
                              Hide
                              <FaChevronUp aria-hidden="true" />
                            </>
                          ) : (
                            <>
                              Manage
                              <FaChevronDown aria-hidden="true" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Privileges Body */}
                    {isExpanded && (
                      <div className="sp-card__body">
                        {isLoadingPrivileges ? (
                          <div className="sp-loading-inline">
                            <FaSpinner className="spinning" />
                            Loading privilege permissions...
                          </div>
                        ) : modules.length === 0 ? (
                          <div className="sp-empty-state" style={{ padding: "30px 20px" }}>
                            <FaInfoCircle className="sp-empty-state__icon" style={{ fontSize: 28 }} />
                            <h4 className="sp-empty-state__title" style={{ fontSize: 14 }}>
                              No Modules Found
                            </h4>
                            <p className="sp-empty-state__text">
                              No assignable system modules are available for this staff member.
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* Inner Filter & Batch Toolbar */}
                            <div className="sp-priv-tools">
                              {/* Inner Module Search */}
                              <div className="sp-priv-tools__search">
                                <FaSearch className="sp-search-icon" aria-hidden="true" />
                                <input
                                  type="text"
                                  className="sp-priv-tools__input"
                                  placeholder="Search modules..."
                                  value={moduleSearchMap[staffId] || ""}
                                  onChange={(e) =>
                                    setModuleSearchMap((prev) => ({
                                      ...prev,
                                      [staffId]: e.target.value,
                                    }))
                                  }
                                  aria-label="Filter module privileges"
                                />
                                {moduleSearchMap[staffId] && (
                                  <button
                                    type="button"
                                    className="sp-clear-search"
                                    onClick={() =>
                                      setModuleSearchMap((prev) => ({
                                        ...prev,
                                        [staffId]: "",
                                      }))
                                    }
                                  >
                                    <FaTimes />
                                  </button>
                                )}
                              </div>

                              {/* Batch Action Buttons */}
                              <div className="sp-priv-tools__batch">
                                <button
                                  type="button"
                                  className="sp-btn sp-btn--outline sp-btn--sm"
                                  onClick={() => openCopyModal(staff)}
                                  title="Copy privileges from another staff member"
                                >
                                  <FaCopy />
                                  Copy From...
                                </button>
                                <button
                                  type="button"
                                  className="sp-btn sp-btn--success sp-btn--sm"
                                  onClick={() =>
                                    handleBatchGrantAll(staffId, staff.staff_name)
                                  }
                                  disabled={isUpdatingBatch || grantedCount === totalModulesCount}
                                  title="Grant all modules to this staff member"
                                >
                                  <FaCheckDouble />
                                  Grant All
                                </button>
                                <button
                                  type="button"
                                  className="sp-btn sp-btn--danger sp-btn--sm"
                                  onClick={() =>
                                    handleBatchRevokeAll(staffId, staff.staff_name)
                                  }
                                  disabled={isUpdatingBatch || grantedCount === 0}
                                  title="Revoke all modules from this staff member"
                                >
                                  <FaBan />
                                  Revoke All
                                </button>
                              </div>
                            </div>

                            {/* Module Cards Grid */}
                            {filteredModules.length === 0 ? (
                              <div
                                className="sp-empty-state"
                                style={{ padding: "24px", background: "#ffffff" }}
                              >
                                <p className="sp-empty-state__title" style={{ fontSize: 14 }}>
                                  No matching modules
                                </p>
                                <p className="sp-empty-state__text">
                                  No modules match your search query.
                                </p>
                              </div>
                            ) : (
                              <div className="sp-modules-grid sp-modules-grid--compact">
                                {filteredModules.map((item) => {
                                  const moduleId = String(item.module_id);
                                  const isGranted = item.status === "Granted";
                                  const rowKey = `${staffId}|${moduleId}`;
                                  const isUpdating = updatingKey === rowKey || isUpdatingBatch;

                                  return (
                                    <div
                                      key={moduleId}
                                      className={`sp-module-card sp-module-card--compact ${isGranted ? "sp-module-card--granted" : ""
                                        }`}
                                    >
                                      <div className="sp-module-card__info">
                                        <div
                                          className="sp-module-card__icon"
                                          aria-hidden="true"
                                        >
                                          {item.icon_img &&
                                            item.icon_img !== "https:///icons/" &&
                                            !item.icon_img.endsWith("/icons/") ? (
                                            <img
                                              src={item.icon_img}
                                              alt=""
                                              className="sp-module-card__icon-img"
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            getModuleIcon(item)
                                          )}
                                        </div>

                                        <div className="sp-module-card__content">
                                          <h4
                                            className="sp-module-card__title"
                                            title={`${item.module_name} · ${isGranted ? "Granted" : "Denied"}`}
                                          >
                                            {item.module_name}
                                          </h4>
                                        </div>
                                      </div>

                                      {/* Custom Toggle Switch */}
                                      <label
                                        className="sp-switch"
                                        title={`${isGranted ? "Revoke" : "Grant"} ${item.module_name
                                          }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isGranted}
                                          disabled={isUpdating}
                                          onChange={() =>
                                            handleTogglePrivilege(
                                              staffId,
                                              moduleId,
                                              item.status
                                            )
                                          }
                                          aria-label={`${isGranted ? "Revoke" : "Grant"} ${item.module_name
                                            }`}
                                        />
                                        <span className="sp-switch__slider" />
                                        {isUpdating && (
                                          <FaSpinner className="sp-switch__spinner spinning" />
                                        )}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Copy Privileges Modal Dialog */}
      {copyModalOpen && targetStaff && (
        <div
          className="sp-modal-backdrop"
          onClick={() => setCopyModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="copy-modal-title"
        >
          <div
            className="sp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-modal__header">
              <h3 id="copy-modal-title" className="sp-modal__title">
                <FaCopy style={{ color: "#4f46e5" }} />
                Copy Privileges to {targetStaff.staff_name}
              </h3>
              <button
                type="button"
                className="sp-modal__close"
                onClick={() => setCopyModalOpen(false)}
                aria-label="Close dialog"
              >
                <FaTimes />
              </button>
            </div>

            <div className="sp-modal__body">
              <p style={{ fontSize: 13.5, color: "#475569", marginBottom: 16 }}>
                Select a staff member whose permission configuration will be copied to{" "}
                <strong>{targetStaff.staff_name}</strong> ({targetStaff.staff_role || "Staff"}).
              </p>

              <label
                htmlFor="sourceStaffSelect"
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: 6,
                }}
              >
                Copy permissions from:
              </label>

              <select
                id="sourceStaffSelect"
                className="sp-filter-select"
                style={{ width: "100%", height: 42 }}
                value={selectedSourceStaffId}
                onChange={(e) => setSelectedSourceStaffId(e.target.value)}
              >
                <option value="">-- Choose source staff member --</option>
                {staffList
                  .filter((s) => String(s.staff_id) !== String(targetStaff.staff_id))
                  .map((s) => (
                    <option key={s.staff_id} value={s.staff_id}>
                      {s.staff_name} ({s.staff_role || "Staff"} - ID: {s.staff_id})
                    </option>
                  ))}
              </select>

              {selectedSourceStaffId && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    fontSize: 12.5,
                    color: "#166534",
                  }}
                >
                  <FaCheckCircle style={{ marginRight: 6 }} />
                  This will replicate all enabled & disabled modules from the selected staff member.
                </div>
              )}
            </div>

            <div className="sp-modal__footer">
              <button
                type="button"
                className="sp-btn sp-btn--outline"
                onClick={() => setCopyModalOpen(false)}
                disabled={copyingPrivileges}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sp-btn sp-btn--primary"
                onClick={executeCopyPrivileges}
                disabled={!selectedSourceStaffId || copyingPrivileges}
              >
                {copyingPrivileges ? (
                  <>
                    <FaSpinner className="spinning" />
                    Copying Privileges...
                  </>
                ) : (
                  <>
                    <FaCopy />
                    Apply Privileges
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPrivilege;
